import { Component } from '@theme/component';

/**
 * Review Quote Carousel component. Shows one quote slide at a time; navigating swaps
 * `hidden` on the slides and plays a fade-in-from-the-right animation on the incoming slide.
 *
 * @typedef {object} ReviewQuoteCarouselRefs
 * @property {HTMLElement[]} slides - The individual quote slides
 * @property {HTMLButtonElement} [previous] - The previous arrow button
 * @property {HTMLButtonElement} [next] - The next arrow button
 * @property {HTMLElement} [counter] - The "01/02" pagination counter
 *
 * @extends {Component<ReviewQuoteCarouselRefs>}
 */
export class ReviewQuoteCarousel extends Component {
  requiredRefs = ['slides'];

  activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.updateControls();
  }

  next() {
    this.goTo(this.activeIndex + 1);
  }

  previous() {
    this.goTo(this.activeIndex - 1);
  }

  /** @param {number} index */
  goTo(index) {
    const slides = this.refs.slides;
    const maxIndex = slides.length - 1;
    const clampedIndex = Math.max(0, Math.min(index, maxIndex));

    if (clampedIndex === this.activeIndex) return;

    const incomingSlide = slides[clampedIndex];
    slides[this.activeIndex].hidden = true;
    incomingSlide.hidden = false;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion) {
      incomingSlide.animate(
        [
          { opacity: 0, transform: 'translateX(32px)' },
          { opacity: 1, transform: 'translateX(0)' },
        ],
        { duration: 400, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );
    }

    this.activeIndex = clampedIndex;
    this.updateControls();
  }

  updateControls() {
    const total = this.refs.slides.length;

    if (this.refs.previous) this.refs.previous.disabled = this.activeIndex <= 0;
    if (this.refs.next) this.refs.next.disabled = this.activeIndex >= total - 1;

    if (this.refs.counter) {
      const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');
      this.refs.counter.textContent = `${pad(this.activeIndex + 1)}/${pad(total)}`;
    }
  }
}

if (!customElements.get('review-quote-carousel')) {
  customElements.define('review-quote-carousel', ReviewQuoteCarousel);
}
